import Media from '../models/media';

/**
 * @ngdoc service
 * @name uploadQueue
 * @description
 * The idea of this service is to handle the uploading process of Images to the previwer service.
 *
 * @memberof common
 */
class UploadQueue {
  /**
   * Class constructor.
   * @param {Service} $q                    To return the allSettled promises.
   * @param {Object}  appConstant           To get the allowed number of items in the queue.
   * @param {Service} appUtils              To be able to generate the files GUID.
   * @param {Service} contentCreatorAppAPI  To upload the media.
   * @param {Service} previewer             We use this service to generate
   *                                        the `image` Object of the new Media.
   * @param {Object}  MEDIA_STATUSES        Used to set the status of the media in the queue.
   *
   * @ignore
   */
  constructor(
    $q,
    appConstant,
    appUtils,
    contentCreatorAppAPI,
    previewer,
    MEDIA_STATUSES,
  ) {
    'ngInject';

    /**
     * The local reference to the `$q` service.
     * @type {Object}
     */
    this.$q = $q;
    /**
     * The local reference to the `appUtils` service.
     * @type {Service}
     */
    this.appUtils = appUtils;
    /**
     * The local reference to the `contentCreatorAppAPI` service.
     * @type {Service}
     */
    this.contentCreatorAppAPI = contentCreatorAppAPI;
    /**
     * The local reference to the `previewer` service.
     * @type {Service}
     */
    this.previewer = previewer;
    /**
     * The local reference to the media statuses object.
     * @type {Object}
     */
    this.MEDIA_STATUSES = MEDIA_STATUSES;
    /**
     * The queue itself.
     * @type {Array}
     */
    this.queue = [];
    /**
     * The max limit of simultaneous uploads.
     * @type {Number}
     */
    this.maxQueueItems = appConstant.maxQueueItems;
    /**
     * Define if we're waiting for a service to complete a task or not.
     * @type {Boolean}
     */
    this.isWorking = false;
    /**
     * Reference of number of Media that are currently being upload,
     * used to get the upload % when the user start
     * the upload proccess;
     * @type {Number}
     */
    this.currentUploadTotal = 0;
  }
  /**
   * Send a Media to the contentCreatorAppAPI
   * @param  {Object} Media the media to be uploaded.
   * @return {Promise}
   */
  _uploadMedia(media) {
    return this.contentCreatorAppAPI.uploadMedia(
      media.images.normal,
      media.images.thumbnail,
      media.campaign.id,
      media.caption,
    )
    .then((response) => {
      this.removeItem(media);
      return response;
    });
  }
  /**
   * Create a new Media Object and include it into the queue.
   * @param {FileList} files         selected by the user.
   * @param {Object}   mediaCampaign a campaign Object to assign to the Media; optional.
   *
   * @return {Promise}
   */
  addItems(selectedFiles, mediaCampaign = {}) {
    this.isWorking = true;

    const workingMedia = [...selectedFiles].map((fileObject) => new Media({
      id: this.appUtils.getGUID(),
      campaign: mediaCampaign,
      file: fileObject,
      status: this.MEDIA_STATUSES.init,
    }));

    // Update the queue with the new media.
    this.queue = [
      ...workingMedia,
      ...this.queue,
    ];

    /**
     * Create the media's preview.
     * Each promise will response with the a new set of settings for the Media Object;
     * this settings includes the properties Status and Image
     */
    const promises = this.previewer.createPreview(workingMedia)
    .map((promise, index) => promise.then((mediaProperty) => {
      mediaProperty.status = mediaProperty.status === this.MEDIA_STATUSES.previewOk
        ? this.MEDIA_STATUSES.ok
        : mediaProperty.status;
      workingMedia[index].setData(mediaProperty);
    }));

    return this.$q.allSettled(promises).then(() => {
      // Once all the promises were resolve, we can stop working.
      this.isWorking = false;
    });
  }
  /**
   * Send a list of Media to the contentCreatorAppAPI
   * @param {Array} media list of media to save.
   * @return {Promise}
   */
  finishUpload(mediaList) {
    this.currentUploadTotal = mediaList.length;

    const promises = mediaList.map((media) => this._uploadMedia(media));
    return this.$q.allSettled(promises)
    .then((responses) => {
      this.currentUploadTotal = 0;
      return responses.map((response) => {
        /**
         * The response object structure will be filled based on the
         * result of the each response.
         * @type {Object}
         */
        const responseItem = {
          error: false,
          message: '',
          data: {},
        };

        if (response.state !== 'fulfilled') {
          responseItem.error = true;
          responseItem.essage = response.reason.statusText;
        } else {
          responseItem.data = response.value;
        }
        return responseItem;
      });
    });
  }
  /**
   * Return percentage of upload progress;
   * @return {Number} the % of media uploaded.
   */
  getUploadProgress() {
    const onReadyPercentage = 100;
    // If the queue is empty then we must have finished uploading
    return this.queue.length === 0 ?
      onReadyPercentage :
      ((this.currentUploadTotal - this.queue.length) / this.currentUploadTotal) * onReadyPercentage;
  }
  /**
   * Return the list of Media ready to be send to the Service;
   * @return {Array}
   */
  getValidMediaInQueue() {
    return this.queue.filter((media) => media.status === this.MEDIA_STATUSES.ok);
  }
  /**
   * Return the number of items which are out of range
   * for the Content Creator API.
   * @return {Number}
   */
  outOflimits() {
    const diff = this.queue.length - this.maxQueueItems;
    return diff > 0 ? diff : 0;
  }
  /**
   * Remove a Media from the queue.
   * @param {Object} media media to be removed.
   */
  removeItem(media) {
    /**
     * @todo update the previewer service to remove assets and use this method here.
     */
    this.queue.splice(this.queue.findIndex((item) => item.id === media.id), 1);
  }
}

export default UploadQueue;



// WEBPACK FOOTER //
// ./src/frontend/modules/common/services/uploadQueue.js